/**
 * PredictiveSearch - Unified search using MeiliSearch for products
 * and Shopify's predictive search for collections/pages
 * 
 * This uses the same MeiliSearch configuration as the main search page
 * (section-collection-product-grid-api.js) to ensure consistent results.
 */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedMeilisearchProducts = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    
    this.meilisearchConfig = window.PredictiveSearchConfig || null;
    this.meilisearchClient = null;
    
    this.initMeilisearch();

    this.setupEventListeners();
  }

  /**
   * Initialize MeiliSearch client using the same configuration as main search page
   */
  initMeilisearch() {
    if (!this.meilisearchConfig || !this.meilisearchConfig.useMeilisearchForProducts) {
      console.log('[PredictiveSearch] MeiliSearch config not found or disabled, using fallback');
      return;
    }

    // MeiliSearch client will be initialized on first search
    // This avoids loading issues if instant-meilisearch.js hasn't loaded yet
    this.meilisearchHost = this.meilisearchConfig.meilisearchHost;
    this.meilisearchApiKey = this.meilisearchConfig.meilisearchApiKey;
    this.meilisearchIndex = this.meilisearchConfig.meilisearchIndex;
    this.currentLocale = this.meilisearchConfig.currentLocale || 'de';
    this.productsLimit = this.meilisearchConfig.productsLimit || 6;
    this.customerPricing = this.meilisearchConfig.customerPricing || null;
    this.initCustomerPricing();

    console.log('[PredictiveSearch] MeiliSearch config loaded:', {
      host: this.meilisearchHost,
      index: this.meilisearchIndex,
      locale: this.currentLocale
    });
  }

  /**
   * Initialize customer-specific (discount group) pricing once customer-pricing.js has loaded
   */
  initCustomerPricing() {
    if (this.customerPricing && window.CustomerPricing && !this._customerPricingReady) {
      window.CustomerPricing.init(this.customerPricing);
      this._customerPricingReady = true;
    }
  }

  /**
   * Get or create MeiliSearch client (lazy initialization)
   */
  getMeilisearchClient() {
    if (this.meilisearchClient) return this.meilisearchClient;
    
    // Check if MeiliSearch JS is available
    if (typeof window.meilisearch !== 'undefined') {
      this.meilisearchClient = new window.meilisearch.MeiliSearch({
        host: this.meilisearchHost,
        apiKey: this.meilisearchApiKey
      });
      return this.meilisearchClient;
    }
    
    return null;
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.querySelector('.reset__button ')?.addEventListener('click', this.closeSearchModal.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      $('.search_suggestion_wrapper').show();
      $('.collection-filter-wrapper').html('');
      $('.page-filter-wrapper').html('');
      return;
    }else{
      $('.collection-filter-wrapper').html('');
      $('.search_suggestion_wrapper').hide();
      $('.page-filter-wrapper').html('');
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.pendingResults = null;
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if(window.innerWidth < 768 && $('.af-template-search').length > 0){
      // auto scroll to next element
      var pagehandle = $('.af-template-search').offset().top-100;
      $('html, body').animate({
      scrollTop: pagehandle
      }, 10);
    }

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
    console.log('onFocus');
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length){
      this.close(true);
      this.closeSearchModal(true);
    } 
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  /**
   * Main search method - fetches results from both MeiliSearch (products) 
   * and Shopify predictive search (collections/pages)
   * Results are shown progressively as each response arrives
   */
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    // Check if we have full cached results
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Abort any previous requests and clear pending results
    this.abortController.abort();
    this.abortController = new AbortController();
    this.pendingResults = null;

    // Track loading state for progressive rendering
    this.pendingResults = {
      shopify: null,
      meilisearch: null,
      shopifyLoaded: false,
      meilisearchLoaded: false,
      productsRendered: false,
      shopifyRendered: false
    };

    // Show skeleton and open results immediately
    this.showSkeletonLoading();
    this.open();

    // Fetch products from MeiliSearch (usually faster)
    this.searchMeilisearchProducts(searchTerm)
      .then((products) => {
        if (!this.pendingResults) return; // Search was aborted
        this.pendingResults.meilisearch = products;
        this.pendingResults.meilisearchLoaded = true;
        this.renderProgressiveResults(queryKey);
      })
      .catch((error) => {
        if (error?.code === 20 || error.name === 'AbortError') return;
        if (!this.pendingResults) return; // Search was aborted
        console.error('[PredictiveSearch] MeiliSearch error:', error);
        this.pendingResults.meilisearch = [];
        this.pendingResults.meilisearchLoaded = true;
        this.renderProgressiveResults(queryKey);
      });

    // Fetch collections and pages from Shopify's predictive search
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[options][fields]=title,variants.sku,product_type,variants.title&limit=20`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        if (!this.pendingResults) return; // Search was aborted
        const markup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.pendingResults.shopify = markup;
        this.pendingResults.shopifyLoaded = true;
        this.renderProgressiveResults(queryKey);
      })
      .catch((error) => {
        if (error?.code === 20 || error.name === 'AbortError') return;
        if (!this.pendingResults) return; // Search was aborted
        console.error('[PredictiveSearch] Shopify search error:', error);
        this.pendingResults.shopifyLoaded = true;
        this.renderProgressiveResults(queryKey);
      });
  }

  /**
   * Show skeleton loading state
   */
  showSkeletonLoading() {
    // Create initial skeleton structure
    const skeletonHtml = `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
          <div class="predictive-search__result-group">
            <div class="predictive-search__pages-wrapper predictive-search__collections" data-loading="true">
              <div class="predictive-search__skeleton-section predictive-search__skeleton-section--categories">
                <div class="predictive-search__skeleton-heading"></div>
                <div class="predictive-search__skeleton-items">
                  ${Array(4).fill('<div class="predictive-search__skeleton-item predictive-search__skeleton-item--category"><div class="predictive-search__skeleton-image"></div><div class="predictive-search__skeleton-title"></div></div>').join('')}
                </div>
              </div>
            </div>
            <div class="predictive-search__pages-wrapper predictive-search__products" data-loading="true">
              <div class="predictive-search__skeleton-section predictive-search__skeleton-section--products">
                <div class="predictive-search__skeleton-heading"></div>
                <div class="predictive-search__skeleton-items">
                  ${Array(4).fill('<div class="predictive-search__skeleton-item predictive-search__skeleton-item--product"><div class="predictive-search__skeleton-product-image"></div><div class="predictive-search__skeleton-product-title"></div><div class="predictive-search__skeleton-product-price"></div></div>').join('')}
                </div>
              </div>
            </div>
            <div class="predictive-search__pages-wrapper predictive-search__pages" data-loading="true">
              <div class="predictive-search__skeleton-section predictive-search__skeleton-section--pages">
                <div class="predictive-search__skeleton-heading"></div>
                <div class="predictive-search__skeleton-items">
                  ${Array(4).fill('<div class="predictive-search__skeleton-item predictive-search__skeleton-item--page"><div class="predictive-search__skeleton-page-title"></div></div>').join('')}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
            ${this.meilisearchConfig?.translations?.searchAll || 'Search all'}
          </button>
          <button type="button" class="search-modal__close-button modal__close-button link link--text focus-inset" aria-label="Close">
            ${this.meilisearchConfig?.translations?.cancel || 'Cancel'}
          </button>
        </div>
      </div>
    `;
    this.predictiveSearchResults.innerHTML = skeletonHtml;
    this.setAttribute('results', true);
  }

  /**
   * Render results progressively as they arrive
   */
  renderProgressiveResults(queryKey) {
    // Guard against race conditions if search was aborted
    if (!this.pendingResults) return;
    
    const { shopify, meilisearch, shopifyLoaded, meilisearchLoaded, productsRendered, shopifyRendered } = this.pendingResults;

    // Render MeiliSearch products if loaded and not yet rendered
    if (meilisearchLoaded && !productsRendered) {
      this.pendingResults.productsRendered = true;
      this.renderProductsSection(meilisearch || []);
    }

    // Render Shopify collections and pages if loaded and not yet rendered
    if (shopifyLoaded && !shopifyRendered) {
      this.pendingResults.shopifyRendered = true;
      if (shopify) {
        this.renderShopifySections(shopify);
      } else {
        // Hide collections and pages if Shopify failed
        this.hideShopifySections();
      }
    }

    // If both are loaded, finalize and cache
    if (shopifyLoaded && meilisearchLoaded) {
      this.finalizeResults(queryKey);
    }
  }

  /**
   * Hide Shopify sections when fetch fails
   */
  hideShopifySections() {
    const collectionsWrapper = this.querySelector('.predictive-search__collections');
    const pagesWrapper = this.querySelector('.predictive-search__pages');
    
    if (collectionsWrapper) {
      collectionsWrapper.removeAttribute('data-loading');
      collectionsWrapper.classList.add('hidden');
    }
    if (pagesWrapper) {
      pagesWrapper.removeAttribute('data-loading');
      pagesWrapper.classList.add('hidden');
    }
  }

  /**
   * Render products section from MeiliSearch results
   */
  renderProductsSection(products) {
    const productsWrapper = this.querySelector('.predictive-search__products');
    if (!productsWrapper) return;

    // Skip if already rendered (no longer loading)
    if (!productsWrapper.hasAttribute('data-loading')) return;

    // Remove loading state
    productsWrapper.removeAttribute('data-loading');

    if (products.length === 0) {
      productsWrapper.classList.add('hidden');
      return;
    }

    const productsHtml = this.renderMeilisearchProducts(products);
    productsWrapper.innerHTML = `
      <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">
        ${this.meilisearchConfig?.translations?.products || 'Products'}
        <input type="checkbox" name="type" class="hidden" value="predictive-search-products">
        <button class="link view_all_link">${this.meilisearchConfig?.translations?.viewAll || 'View all'}</button>
      </h2>
      <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
        ${productsHtml}
      </ul>
    `;
    productsWrapper.classList.remove('hidden');
  }

  /**
   * Render collections and pages from Shopify results
   */
  renderShopifySections(shopifyMarkup) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${shopifyMarkup}</div>`, 'text/html');
    const container = doc.body.firstChild;

    // Extract collections section
    const shopifyCollections = container.querySelector('.predictive-search__collections');
    const collectionsWrapper = this.querySelector('.predictive-search__collections');
    
    if (collectionsWrapper && collectionsWrapper.hasAttribute('data-loading')) {
      collectionsWrapper.removeAttribute('data-loading');
      
      if (shopifyCollections) {
        const collectionsList = shopifyCollections.querySelector('.predictive-search__results-list');
        const hasCollections = collectionsList && collectionsList.children.length > 0;
        
        if (hasCollections) {
          collectionsWrapper.innerHTML = shopifyCollections.innerHTML;
          collectionsWrapper.classList.remove('hidden');
          
          // Update collection filter wrapper on search page
          if ($('body').hasClass('template-search')) {
            $('.collection-filter-wrapper').html(shopifyCollections.innerHTML);
            $('.collection-filter-wrapper .predictive-search__heading').remove();
          }
        } else {
          collectionsWrapper.classList.add('hidden');
          if ($('body').hasClass('template-search')) {
            $('.collection-filter-wrapper').html('');
          }
        }
      } else {
        collectionsWrapper.classList.add('hidden');
      }
    }

    // Extract pages section
    const shopifyPages = container.querySelector('.predictive-search__pages');
    const pagesWrapper = this.querySelector('.predictive-search__pages');
    
    if (pagesWrapper && pagesWrapper.hasAttribute('data-loading')) {
      pagesWrapper.removeAttribute('data-loading');
      
      if (shopifyPages) {
        const pagesList = shopifyPages.querySelector('.predictive-search__results-list');
        const hasPages = pagesList && pagesList.children.length > 0;
        
        if (hasPages) {
          pagesWrapper.innerHTML = shopifyPages.innerHTML;
          pagesWrapper.classList.remove('hidden');
          
          // Update page filter wrapper on search page
          if ($('body').hasClass('template-search')) {
            $('.page-filter-wrapper').html(shopifyPages.innerHTML);
            $('.page-filter-wrapper .predictive-search__heading').remove();
          }
        } else {
          pagesWrapper.classList.add('hidden');
          if ($('body').hasClass('template-search')) {
            $('.page-filter-wrapper').html('');
          }
        }
      } else {
        pagesWrapper.classList.add('hidden');
      }
    }
  }

  /**
   * Finalize results after all data is loaded
   */
  finalizeResults(queryKey) {
    this.removeAttribute('loading');
    this.setLiveRegionResults();

    // Cache the final HTML for future use (only if results wrapper exists and has content)
    const resultsWrapper = this.querySelector('#predictive-search-results');
    if (resultsWrapper && resultsWrapper.innerHTML.trim()) {
      const cachedHtml = resultsWrapper.outerHTML;
      this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
        predictiveSearchInstance.cachedResults[queryKey] = cachedHtml;
      });
    }

    // Clean up pending results
    this.pendingResults = null;
  }

  /**
   * Search MeiliSearch for products using the same configuration as main search page
   * Uses the same /multi-search endpoint, payload structure, and query logic
   */
  async searchMeilisearchProducts(searchTerm) {
    // Check cache first
    const cacheKey = searchTerm.toLowerCase();
    if (this.cachedMeilisearchProducts[cacheKey]) {
      return this.cachedMeilisearchProducts[cacheKey];
    }

    // If MeiliSearch config not available, return empty
    if (!this.meilisearchConfig || !this.meilisearchConfig.useMeilisearchForProducts) {
      return [];
    }

    try {
      // Use the same /multi-search endpoint as the main search page
      // This matches the exact payload structure from section-collection-product-grid-api.js
      const response = await fetch(`${this.meilisearchHost}/multi-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.meilisearchApiKey}`
        },
        body: JSON.stringify({
          queries: [
            {
              indexUid: this.meilisearchIndex,
              q: searchTerm,
              // Same facets as main search page
              facets: [
                'color_hex',
                this.getMaterialAttribute(),
                'size_options'
              ],
              // Same highlight settings as main search page
              attributesToHighlight: ['*'],
              highlightPreTag: '__ais-highlight__',
              highlightPostTag: '__/ais-highlight__',
              // Limit for predictive search
              hitsPerPage: this.productsLimit,
              // Page is 1-indexed in MeiliSearch InstantSearch adapter
              page: 1,
              // Same sort as main search page: isNew:desc, isSale:asc, release_start_time:desc
              sort: ['isNew:desc', 'isSale:asc', 'release_start_time:desc']
            }
          ]
        }),
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`MeiliSearch error: ${response.status}`);
      }

      const data = await response.json();
      // multi-search returns results array
      const products = data.results?.[0]?.hits || [];
      
      // Cache the results
      this.cachedMeilisearchProducts[cacheKey] = products;
      
      console.log(`[PredictiveSearch] MeiliSearch returned ${products.length} products for "${searchTerm}"`);
      return products;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      console.error('[PredictiveSearch] MeiliSearch error:', error);
      return [];
    }
  }

  /**
   * Get the material attribute name based on current locale
   * Same logic as section-collection-product-grid-api.js
   */
  getMaterialAttribute() {
    const materialAttributes = {
      'en': 'material_names_en',
      'de': 'material_names_de',
      'fr': 'material_names_fr',
      'it': 'material_names_it'
    };
    return materialAttributes[this.currentLocale] || materialAttributes['de'];
  }

  /**
   * Merge Shopify predictive search results (collections/pages) with MeiliSearch products
   * Order: Collections -> Products -> Pages (same as original)
   */
  mergeSearchResults(shopifyMarkup, meilisearchProducts) {
    // Parse the Shopify markup
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${shopifyMarkup}</div>`, 'text/html');
    const container = doc.body.firstChild;

    // Find existing sections
    let productsSection = container.querySelector('.predictive-search__products');
    const collectionsSection = container.querySelector('.predictive-search__collections');
    const pagesSection = container.querySelector('.predictive-search__pages');
    const groupsWrapper = container.querySelector('#predictive-search-results-groups-wrapper .predictive-search__result-group');

    // If we have MeiliSearch products, replace/create the products section
    if (meilisearchProducts && meilisearchProducts.length > 0) {
      const productsHtml = this.renderMeilisearchProducts(meilisearchProducts);
      
      if (productsSection) {
        // Update existing products section
        const listEl = productsSection.querySelector('.predictive-search__results-list');
        if (listEl) {
          listEl.innerHTML = productsHtml;
        }
        productsSection.classList.remove('hidden');
      } else if (groupsWrapper) {
        // Create new products section
        const newProductsSection = document.createElement('div');
        newProductsSection.className = 'predictive-search__pages-wrapper predictive-search__products';
        newProductsSection.innerHTML = `
          <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">
            ${this.meilisearchConfig?.translations?.products || 'Products'}
            <input type="checkbox" name="type" class="hidden" value="predictive-search-products">
            <button class="link view_all_link">${this.meilisearchConfig?.translations?.viewAll || 'View all'}</button>
          </h2>
          <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
            ${productsHtml}
          </ul>
        `;
        
        // Insert products BEFORE pages section (after collections)
        // Order should be: Collections -> Products -> Pages
        if (pagesSection) {
          pagesSection.parentNode.insertBefore(newProductsSection, pagesSection);
        } else if (collectionsSection) {
          // Insert after collections
          collectionsSection.parentNode.insertBefore(newProductsSection, collectionsSection.nextSibling);
        } else {
          // No collections or pages, just append
          groupsWrapper.appendChild(newProductsSection);
        }
        
        productsSection = newProductsSection;
      }
    } else if (productsSection) {
      // Hide products section if no results
      productsSection.classList.add('hidden');
    }

    // Ensure correct order: Collections -> Products -> Pages
    if (groupsWrapper && productsSection && pagesSection) {
      // Make sure products comes before pages
      if (productsSection.compareDocumentPosition(pagesSection) & Node.DOCUMENT_POSITION_PRECEDING) {
        // Pages is before products, need to reorder
        pagesSection.parentNode.insertBefore(productsSection, pagesSection);
      }
    }

    return container.innerHTML;
  }

  /**
   * Render MeiliSearch products as HTML (same format as Shopify predictive search)
   */
  renderMeilisearchProducts(products) {
    const locale = this.currentLocale || 'de';
    const currency = Shopify?.currency?.active || 'CHF';
    
    return products.map((product, index) => {
      const title = this.getLocalizedTitle(product);
      let price = (product.min_price || product.max_price || 0).toFixed(2);
      let compareAtPrice = (product.min_compare_at_price || product.max_compare_at_price || 0).toFixed(2);
      // Apply customer-specific discount group pricing if active
      this.initCustomerPricing();
      if (window.CustomerPricing && window.CustomerPricing.isActive()) {
        const customerResult = window.CustomerPricing.calculatePrice(product);
        if (customerResult) {
          price = customerResult.price.toFixed(2);
          compareAtPrice = customerResult.originalPrice.toFixed(2);
        }
      }
      const onSale = compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price);
      const imageUrl = this.getProductImageUrl(product);
      const productUrl = this.getLocalizedProductUrl(product.handle);
      
      return `
        <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="${productUrl}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
            <img class="predictive-search__image" src="${imageUrl}" alt="${this.escapeHtml(title)}" width="540" loading="lazy">
            <div class="predictive-search__item-content">
              <p class="predictive-search__item-heading h5">${this.escapeHtml(title)}</p>
              <div class="price ${onSale ? "price--large price--on-sale" : ""}">
                <div class="price__container">
                  <div class="price__regular">
                    <span class="price-item price-item--regular">${currency} ${price}</span>
                  </div>
                  ${onSale ? `
                    <div class="price__sale">
                      <span class="price-item price-item--sale price-item--last">${currency} ${price}</span>
                      <span>
                        <s class="price-item price-item--regular">${currency} ${compareAtPrice}</s>
                      </span>
                    </div>
                  `: ""}
                </div>
              </div>
            </div>
          </a>
        </li>
      `;
    }).join('');
  }

  /**
   * Get localized product title (same logic as section-collection-product-grid-api.js)
   */
  getLocalizedTitle(product) {
    const locale = this.currentLocale;
    if (locale === 'de') {
      return product.title || 'Product';
    }
    // Try locale-specific field first, fallback to default title
    const localizedField = 'title_' + locale;
    return product[localizedField] || product.title || 'Product';
  }

  /**
   * Get product image URL
   */
  getProductImageUrl(product) {
    if (product.featured_image) {
      return product.featured_image + '&width=540';
    }
    if (product.images && product.images.length > 0) {
      const img = product.images[0];
      const src = typeof img === 'string' ? img : img.src;
      if (src) {
        return src.includes('?') ? src + '&width=540' : src + '?width=540';
      }
    }
    return 'https://cdn.shopify.com/s/files/1/0865/6692/8715/files/product-placeholder.jpg?v=1755017199&width=540';
  }

  /**
   * Get localized product URL
   */
  getLocalizedProductUrl(handle) {
    const locale = this.currentLocale;
    if (locale === 'de') {
      return `/products/${handle}`;
    }
    return `/${locale}/products/${handle}`;
  }

  /**
   * Escape HTML special characters
   */
  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();

    
    let collection_result_length = $('.predictive-search__collections .predictive-search__results-list li').length;
    let page_result_length = $('.predictive-search__pages .predictive-search__results-list li').length;

    if(collection_result_length == 0){
      $('.predictive-search__collections').addClass('hidden');
      if($('body').hasClass('template-search')){
        $('.collection-filter-wrapper').html('');
      }
    }else{
      $('.predictive-search__collections').removeClass('hidden');
      $('.collection-filter-wrapper').html('');
      if($('body').hasClass('template-search')){
        $('.collection-filter-wrapper').html($('.predictive-search__collections').html());
        $('.collection-filter-wrapper .predictive-search__heading').remove();
      }
    }

    if(page_result_length == 0){
      $('.predictive-search__pages').addClass('hidden');
      if($('body').hasClass('template-search')){
        $('.page-filter-wrapper').html('');
      }
    }else{
      $('.predictive-search__collections').removeClass('hidden');
      $('.page-filter-wrapper').html('');
      if($('body').hasClass('template-search')){
        $('.page-filter-wrapper').html($('.predictive-search__pages').html());
        $('.page-filter-wrapper .predictive-search__heading').remove();
      }
    }

    // Products are now handled by MeiliSearch in getSearchResults()
    // No need for the old AICO fallback logic
  }

  jsonToFormData(json) {
    const formData = new FormData();

    for (const key in json) {
      if (json.hasOwnProperty(key)) {
        const value = json[key];
        if (Array.isArray(value)) {
          value.forEach(item => formData.append(key, item));
        } else if (typeof value === 'object' && value !== null) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      }
    }
    return formData;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.closeSuggestion();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.closeSuggestion();
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    // this.removeAttribute('open');
    // this.input.setAttribute('aria-expanded', false);
    // this.resultsMaxHeight = false;
    // this.predictiveSearchResults.removeAttribute('style');
    // this.closeSuggestion();
  }

  closeSearchModal(clearSearchTerm = false){
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.closeSuggestion();
    $('.search_suggestion_wrapper').show();
    $('.collection-filter-wrapper').html('');
    $('.page-filter-wrapper').html('');
  }

  closeSuggestion(){
    if ($('.predictive-search--header').attr('style')) {
      $('.search_suggestion_wrapper').addClass('hidden');
    }else{
      $('.search_suggestion_wrapper').removeClass('hidden');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
